import { audioEngine } from './AudioEngine';

/**
 * EngineSound.ts — the player's continuous engine note (M14: dropped a fat
 * octave, owner: "lower bass, like a Ferrari"): a tightly-detuned saw pair for
 * the growl, a STRONG half-pitch sine sub for the chest, and a darker lowpass.
 * Pitch tracks speed, gain tracks load; boost opens the filter for the snarl.
 */
export class EngineSound {
  private built = false;
  private oscA: OscillatorNode | null = null;
  private oscB: OscillatorNode | null = null;
  private sub: OscillatorNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private subGain: GainNode | null = null;
  private gain: GainNode | null = null;

  private ensure(): boolean {
    const ctx = audioEngine.context;
    const bus = audioEngine.sfxBus;
    if (!ctx || !bus) return false;
    if (this.built) return true;
    this.oscA = ctx.createOscillator();
    this.oscB = ctx.createOscillator();
    this.sub = ctx.createOscillator();
    this.oscA.type = 'sawtooth';
    this.oscB.type = 'sawtooth';
    this.sub.type = 'sine';
    this.oscB.detune.value = 9; // slight beat between the pair = mechanical
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 360;
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.subGain = ctx.createGain();
    this.subGain.gain.value = 1.7; // the sub carries the chest of the note
    this.oscA.connect(this.filter);
    this.oscB.connect(this.filter);
    this.sub.connect(this.subGain).connect(this.gain); // sub bypasses the filter — always warm
    this.filter.connect(this.gain);
    this.gain.connect(bus);
    this.oscA.start();
    this.oscB.start();
    this.sub.start();
    this.built = true;
    return true;
  }

  /** Per-frame: speed01 = speed / topSpeed, load = |throttle|, boosting. */
  update(speed01: number, load: number, boosting: boolean, racing: boolean): void {
    if (!this.ensure()) return;
    const ctx = audioEngine.context;
    if (!ctx || !this.oscA || !this.oscB || !this.sub || !this.filter || !this.gain) return;
    const t = ctx.currentTime;
    const s = Math.max(0, Math.min(1.2, speed01));
    // Roughly an octave below the old note: idles ~34 Hz, tops out ~118 Hz.
    const hz = 34 + 70 * s + (boosting ? 14 : 0);
    this.oscA.frequency.setTargetAtTime(hz, t, 0.06);
    this.oscB.frequency.setTargetAtTime(hz * 2.01, t, 0.06); // octave growl, barely sharp
    this.sub.frequency.setTargetAtTime(hz * 0.5, t, 0.08);
    this.filter.frequency.setTargetAtTime(boosting ? 1500 : 260 + 520 * s, t, 0.09);
    const idle = racing ? 0.05 : 0;
    const vol = idle + (0.1 + 0.16 * s) * Math.max(load, s * 0.55) * (racing ? 1 : 0);
    this.gain.gain.setTargetAtTime(Math.min(0.24, vol), t, 0.08);
  }

  stop(): void {
    if (!this.gain || !audioEngine.context) return;
    this.gain.gain.setTargetAtTime(0, audioEngine.context.currentTime, 0.1);
  }
}
